import axiosClient from '@clients/axiosClient';

type IGetAddressDetails = { accountId: string };
type IGetPhoneDetails = { accountId: number };
type IGetAccountDetails = { accountId: number };
type IAccountSearchDocumentNumber = { documentNumber: string };
export interface IPatchUpdateAccountStatus {
    accountId: number;
    dataToSubmit: IPatchUpdateAccountStatusDetails;
}
export interface IPatchUpdateLocationDetails {
    accountId: number;
    addressId: number;
    dataToSubmit: AddressDetails;
}
export interface IAddLocationDetails {
    accountId: number;
    dataToSubmit: ILocationFormInputs;
}
export interface ILocationFormInputs {
    type: string;
    address: string;
    complementary_address: string;
    city: string;
    neighborhood: string;
    zip_code: string;
    country: string;
    mailing_address: boolean;
}

export interface IAddPhoneDetails {
    account_id: number;
    customer_id: number;
    dataToSubmit: Partial<IPhoneDetailsFormInputs>;
}

export interface IPatchUpdatePhoneDetails {
    accountId: number;
    phoneId: number;
    dataToSubmit: Partial<IPhoneDetailsFormInputs>;
}
export interface IPhoneDetailsFormInputs {
    area_code: number;
    country_code: number;
    number: string;
    active: boolean;
    extension: string;
    type: string;
}

export interface IPatchUpdateCustomerDetails {
    accountId: number;
    customerId: number;
    dataToSubmit: Partial<ICustomerDetailsFormInputs>;
}
export interface ICustomerDetailsFormInputs {
    printed_name: string;
    social_name: string;
    nickname: string;
    email: string;
    gender: string;
    mothers_name: string;
    birth_date: string;
    marital_status: string;
    name: string;
}

export interface IPatchUpdateAccountStatusDetails {
    status: string;
    reason_id?: number;
    description?: string;
    update_children?: boolean;
}
type IGetLimitsDetails = { accountId: number };

export type AddressDetails = {
    id: number;
    type: string;
    address: string;
    number: number;
    complementary_address: string;
    neighborhood: string;
    zip_code: string;
    city: string;
    state: string;
    country: string;
    creation_date: string;
    mailing_address: boolean;
    active: boolean;
    org_id: string;
    account_id: number;
    last_update: string;
};

export type PhoneDetails = {
    id: number;
    account_id: number;
    area_code: number;
    number: string;
    type: string;
    extension: string;
    active: boolean;
    org_id: string;
    customer_id: number;
    country_code: number;
};

export type DocumentSearchAccount = {
    account_id: number;
    customer_id: number;
    account_status: string;
    acquisition_id: number;
    program_id: number;
};

export type AccountStatus = {
    id: number;
    name: string;
    group: string;
};
export type Reason = {
    code: string;
    reason_id: number;
    description: string;
    account_status_id: number;
    show_user: boolean;
};

export interface AccountDetails {
    current_cicle: number;
    document_number: string;
    postal_address_type: number;
    blocked_amount: number | null;
    social_name: string | null;
    open_due_date: Date | null;
    exchange_mode: string;
    status_reason_id: number | null;
    program_name: string;
    status_reason_description: string | null;
    customer_id: number;
    email: string;
    program_type: string;
    collections_status: string;
    org: string;
    entity_type: string;
    current_balance: number;
    external_id: string;
    creation_date: string;
    program_due_date_id: number;
    account_id: number;
    name: string;
    acquisition_id: number;
    account_status_date: null;
    program_id: number;
    status: string;
    available_total_installment_credit: number;
    available_withdrawal_credit: number;
    total_credit_limit: number;
    available_monthly_credit: number;
    monthly_credit_limit: number;
    withdrawal_credit_limit: number;
    max_credit_limit: number;
    available_limit: number;
    total_installment_credit_limit: number;
}

export interface LimitsDetails {
    max_credit_limit: number;
    available_total_installment_credit: number;
    available_withdrawal_credit: number;
    available_savings_account_limit: number;
    total_overdraft_limit: number;
    total_installment_credit_limit: number;
    withdrawal_credit_limit: number;
    total_credit_limit: number;
    percentage_over_limit: number;
    available_monthly_credit: number;
    available_credit_limit: number;
    monthly_credit_limit: number;
}

export interface IPatchUpdateAccountLimits {
    accountId: number;
    dataToSubmit: IPatchUpdateAccountLimitsDetails;
}

export type IPatchUpdateAccountLimitsDetails = {
    limit: number;
    total_overdraft_limit: number;
    percentage_over_limit: number;
};

const accounts = () => {
    const client = axiosClient();

    return {
        async getAccountDetails({ accountId }: IGetAccountDetails) {
            const { data } = await client.get<AccountDetails>(
                `accounts/v1/accounts/${accountId}`,
                { headers: { 'x-account-id': accountId } },
            );
            return data;
        },
        async getAddressDetails({ accountId }: IGetAddressDetails) {
            const { data } = await client.get<AddressDetails[]>(
                `accounts/v1/accounts/${accountId}/addresses`,
            );
            return data;
        },
        async getPhoneDetails({ accountId }: IGetPhoneDetails) {
            const { data } = await client.get<PhoneDetails[]>(
                `accounts/v1/accounts/${accountId}/phones`,
            );
            return data;
        },
        async accountSearchDocumentNumber({
            documentNumber,
        }: IAccountSearchDocumentNumber) {
            const { data } = await client.post<DocumentSearchAccount[]>(
                `accounts/v3/accounts/search`,
                { document_number: documentNumber },
            );
            return data;
        },
        async getAccountStatuses() {
            const { data } = await client.get<AccountStatus[]>(
                `accounts/v1/account-status`,
            );
            return data;
        },
        async getStatusChangeReasons() {
            const { data } = await client.get<Reason[]>(
                `accounts/v1/accounts-status/reasons`,
            );
            return data;
        },
        async patchUpdateAccountStatus({
            accountId,
            dataToSubmit,
        }: IPatchUpdateAccountStatus) {
            const { data } = await client.patch(
                `accounts/v2/accounts/${accountId}/status`,
                { ...dataToSubmit },
            );
            return data;
        },
        async getLimits({ accountId }: IGetLimitsDetails) {
            const { data } = await client.get<LimitsDetails>(
                `ledger/v1/accounts/${accountId}/limits`,
            );
            return data;
        },
        async patchUpdateLocationDetails({
            accountId,
            addressId,
            dataToSubmit,
        }: IPatchUpdateLocationDetails) {
            const { data } = await client.patch(
                `accounts/v1/accounts/${accountId}/addresses/${addressId}`,
                { ...dataToSubmit },
            );
            return data;
        },
        async addPhoneDetails({
            account_id,
            customer_id,
            dataToSubmit,
        }: IAddPhoneDetails) {
            const { data } = await client.post(
                `accounts/v1/accounts/${account_id}/phones`,
                {
                    ...dataToSubmit,
                },
                {
                    headers: {
                        'x-customer-id': customer_id,
                    },
                },
            );
            return data;
        },
        async patchUpdatePhoneDetails({
            accountId,
            phoneId,
            dataToSubmit,
        }: IPatchUpdatePhoneDetails) {
            const { data } = await client.patch(
                `accounts/v1/accounts/${accountId}/phones/${phoneId}`,
                { ...dataToSubmit },
            );
            return data;
        },
        async patchUpdateUserDetails({
            accountId,
            customerId,
            dataToSubmit,
        }: IPatchUpdateCustomerDetails) {
            const { data } = await client.patch(
                `accounts/v1/accounts/${accountId}/customers/${customerId}/detail`,
                { ...dataToSubmit },
            );
            return data;
        },
        async addNewLocation({ accountId, dataToSubmit }: IAddLocationDetails) {
            const { data } = await client.post(
                `accounts/v1/accounts/${accountId}/addresses`,
                // force default values for fields on form we are not using but are required by API.
                { ...dataToSubmit, number: 0, state: '__' },
            );
            return data;
        },
        async patchUpdateAccountLimits({
            accountId,
            dataToSubmit,
        }: IPatchUpdateAccountLimits) {
            const { data } = await client.patch(
                `accounts/v1/accounts/${accountId}/limit`,
                { ...dataToSubmit },
            );
            return data;
        },
    };
};

export default accounts;
